<template>
  <div>
    <!-- Toolbar: search + filters -->
    <div class="email-toolbar px-1 pt-1">
      <b-form-input v-model="searchQuery" debounce="400" placeholder="Search subject, sender, or content…" class="mb-75" />
      <div class="d-flex email-toolbar__row">
        <v-select
          v-model="directionFilter"
          :options="directionOptions"
          :reduce="(o) => o.value"
          :clearable="false"
          :searchable="false"
          label="label"
          class="email-toolbar__direction mr-75"
        />
        <v-select
          v-model="senderFilter"
          :options="senderOptions"
          :reduce="(o) => o.value"
          label="label"
          placeholder="All senders"
          class="email-toolbar__sender flex-grow-1"
        />
      </div>
      <div class="d-flex align-items-center mt-50">
        <b-spinner v-if="isLoading" small class="mr-50" />
        <small class="text-muted">{{ totalResults }} {{ totalResults === 1 ? 'conversation' : 'conversations' }}</small>
      </div>
    </div>

    <!-- Empty state -->
    <p v-if="!isLoading && emailData.length === 0" class="text-center text-muted w-100 my-3">
      {{ hasFilters ? 'No conversations match your filters.' : 'No emails for this student yet.' }}
    </p>

    <!-- Thread list: one row per conversation (latest message shown) -->
    <app-timeline v-else class="w-100 mt-0 m-2">
      <app-timeline-item
        class="w-100"
        v-for="(email, index) in emailData"
        :key="index"
        :variant="email.direction === 'inbound' ? 'success' : 'info'"
        :icon="email.direction === 'inbound' ? 'ArrowDownIcon' : 'ArrowUpIcon'"
        :icon-tooltip="title(email.direction)"
      >
        <div class="d-flex justify-content-between align-items-start">
          <h6 class="email-item__title cursor-pointer mb-25 mr-1" :title="email.subject" @click="openEmail(email)">
            {{ email.subject || '(No subject)' }}
          </h6>
          <div class="d-flex align-items-center flex-shrink-0">
            <b-badge v-if="email.threadCount > 1" variant="light-secondary" pill class="mr-75" title="Messages in this conversation">
              <feather-icon icon="MessageSquareIcon" size="11" class="mr-25" />{{ email.threadCount }}
            </b-badge>
            <small class="text-nowrap text-muted">{{ email.createdAt }}</small>
          </div>
        </div>
        <p class="email-item__sender mb-0 text-muted">{{ email.sender.name }}</p>
      </app-timeline-item>
    </app-timeline>

    <b-pagination
      v-if="totalResults > pageSize"
      v-model="currentPage"
      :total-rows="totalResults"
      :per-page="pageSize"
      class="mx-2 mb-1"
      first-number
      last-number
    ></b-pagination>

    <student-email-modal
      v-if="openEmailData"
      :student-data="studentData"
      :email-id.sync="openEmailId"
      :email-data.sync="openEmailData"
      :is-email-modal-open.sync="isEmailOpen"
      @hide="closeEmail"
    />
  </div>
</template>

<script>
import { BPagination, BFormInput, BSpinner, BBadge, VBTooltipPlugin } from 'bootstrap-vue';
import vSelect from 'vue-select';
import moment from 'moment';
import store from '@/store';
import AppTimeline from '@core/components/app-timeline/AppTimeline.vue';
import AppTimelineItem from '@core/components/app-timeline/AppTimelineItem.vue';
import router from '@/router';
import { computed, ref, watch } from '@vue/composition-api';
import { momentTz } from '@/libs/utils/time';
import { title } from '@core/utils/filter';
import StudentEmailModal from '@/views/apps/student/students-view/components/StudentEmailModal.vue';

export default {
  name: 'StudentSocialItem',
  components: {
    BPagination,
    BFormInput,
    BSpinner,
    BBadge,
    vSelect,
    AppTimeline,
    AppTimelineItem,
    StudentEmailModal,
  },
  directives: {
    'b-tooltip': VBTooltipPlugin,
  },
  props: {
    studentData: {
      type: Object,
    },
    profileData: {
      type: Object,
    },
  },
  mounted() {
    // this.loadEmails();
  },
  setup(props) {
    const isLoading = ref(false);
    const emailData = ref([]);
    const emailCategory = ref(props.category);

    const pageSize = ref(5);
    const totalResults = ref(0);
    const totalPages = ref(0);
    const currentPage = ref(1);

    // Filters
    const searchQuery = ref('');
    const directionFilter = ref(null);
    const senderFilter = ref(null);
    const senderOptions = ref([]);
    const directionOptions = [
      { label: 'All', value: null },
      { label: 'Received', value: 'inbound' },
      { label: 'Sent', value: 'outbound' },
    ];
    const hasFilters = computed(() => !!(searchQuery.value || directionFilter.value || senderFilter.value));

    const openEmailId = ref(null);
    const openEmailData = ref(null);
    const isEmailOpen = computed(() => !!openEmailId.value);
    const openEmail = (email) => {
      openEmailId.value = email.id;
      openEmailData.value = email;
    };
    const closeEmail = () => {
      openEmailId.value = null;
      openEmailData.value = null;
    };

    const refetchData = () => {
      isLoading.value = true;

      const queryParams = {
        limit: pageSize.value,
        page: currentPage.value,
      };
      if (searchQuery.value) queryParams.search = searchQuery.value;
      if (directionFilter.value) queryParams.direction = directionFilter.value;
      if (senderFilter.value) queryParams.senderEmail = senderFilter.value;

      store
        .dispatch('studentStoreModule/fetchStudentEmailThreads', {
          id: router.currentRoute.params.id,
          queryParams,
        })
        .then((response) => {
          totalResults.value = response.data.totalResults;
          totalPages.value = response.data.totalPages;
          emailData.value = response.data.results.map((email) => ({
            ...email,
            receivedAt: moment(email.createdAt).format('L hh:mm A '),
            sentAt: moment(email.createdAt).format('L hh:mm A '),
            createdAt: moment(email.createdAt).format('L hh:mm A '),
            updatedAt: moment(email.updatedAt).format('L hh:mm A '),
          }));
        })
        .catch((error) => {
          if (error.response && error.response.status === 404) {
            emailData.value = [];
            totalResults.value = 0;
          }
        })
        .finally(() => {
          isLoading.value = false;
        });
    };

    const loadSenders = () => {
      store
        .dispatch('studentStoreModule/fetchEmailSenders', { id: router.currentRoute.params.id })
        .then((response) => {
          senderOptions.value = (response.data || []).map((s) => ({
            label: s.name && s.name !== s.email ? `${s.name} (${s.email})` : s.email,
            value: s.email,
          }));
        })
        .catch(() => {});
    };

    refetchData();
    loadSenders();

    watch(currentPage, refetchData);
    // Changing a filter jumps back to page 1 (which refetches); if already on page 1, refetch directly.
    watch([searchQuery, directionFilter, senderFilter], () => {
      if (currentPage.value !== 1) currentPage.value = 1;
      else refetchData();
    });

    return {
      openEmail,
      closeEmail,
      openEmailId,
      openEmailData,
      isEmailOpen,
      isLoading,
      emailData,
      emailCategory,
      searchQuery,
      directionFilter,
      senderFilter,
      senderOptions,
      directionOptions,
      hasFilters,
      totalResults,
      totalPages,
      currentPage,
      pageSize,
      momentTz,
      title,
      refetchData,
    };
  },
};
</script>

<style lang="scss" scoped>
.attr-tag {
  float: right;
  margin: 2.5px 0 2.5px 5px;
}

.email-toolbar {
  &__direction {
    flex: 0 0 8rem;
  }

  // Let the sender select shrink within the row so its long labels truncate
  // instead of overflowing the narrow panel.
  &__sender {
    min-width: 0;
  }

  // Keep selected values on one line (they truncate rather than wrap).
  ::v-deep .vs__selected {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}

// Wrap long subjects (and senders) inside the panel. min-width: 0 lets the
// subject shrink within its flex row instead of pushing past the edge, and
// overflow-wrap: anywhere breaks long unbroken strings such as IDs or URLs.
.email-item__title {
  flex: 1 1 auto;
  min-width: 0;
  white-space: normal;
  overflow-wrap: anywhere;
}

.email-item__sender {
  overflow-wrap: anywhere;
}
</style>
